import { useMutation, useQuery } from '@tanstack/react-query'
import { TopBar } from 'components/Layout/TopBar'
import { TopBarContent } from 'components/Layout/TopBarProvider'
import GradientLoadingAnimation from 'components/Reusables/GradientLoadingAnimation'
import { Button } from 'components/ui/Button'
import { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { CDN_BASE, DEFAULT_CONFIG } from './constants'
import DottedBackground from './DottedBackground'
import { WidgetPersonalization } from './WidgetPersonalization'
import { WidgetSettings } from './WidgetSettings'

export default function WebWidget() {
  const methods = useForm({ defaultValues: DEFAULT_CONFIG })
  const config = methods.watch()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const tab = useMemo(() => {
    return searchParams.get('tab') || 'settings'
  }, [searchParams])

  const tabs = [
    {
      value: 'settings',
      label: 'Settings',
    },
    {
      value: 'dimensions',
      label: 'Dimensions',
    },
  ]

  const handleTabChange = (value) => {
    setSearchParams({ tab: value })
  }

  const { data: pathways, isLoading: isLoadingPathways } = useQuery({
    queryKey: ['pathways'],
    queryFn: async () => {
      const response = await $fetch('/convo_pathway/get', { method: 'GET' })
      return (response?.data?.data || []).flatMap(pathway => pathway.type === 'folder' ? pathway.children : pathway)
    },
  })

  const { data: widgetData, isLoading: isLoadingWidget, refetch: refetchWidget } = useQuery({
    queryKey: ['widget'],
    queryFn: async () => {
      const response = await $fetch('/v1/widgets', { method: 'GET' })
      return response?.data?.[0]
    },
  })

  useEffect(() => {
    if (widgetData) {
      // Set form data
      methods.reset({
        ...widgetData.config,
        id: widgetData.id,
        pathwayId: widgetData.pathway_id,
        widgetId: widgetData.id,
        allowedDomains: widgetData.allowed_domains,
        hideFooter: !!widgetData.config.hideFooter,
        threads: DEFAULT_CONFIG.threads,
      })
    }
  }, [widgetData, methods])

  const widgetExists = !!widgetData

  useEffect(() => {
    const scriptId = 'widget-loader'

    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script')
      script.src = `${CDN_BASE}/web-component/widget-web-component.js`
      script.id = scriptId
      script.type = 'module'
      script.async = true

      // Add it to our widget-config-web-component div
      const configContainer = document.getElementById('widget-config-web-component')
      if (configContainer) {
        configContainer.appendChild(script)
      }
    }
  }, [])

  const { mutate: updateWidget, isPending: isUpdating } = useMutation({
    mutationFn: async (data) => {
      return $fetch(`/v1/widget/${data.id}`, {
        method: 'PATCH',
        body: {
          config: stripDataForConfig(data),
          pathway_id: data.pathwayId,
          allowed_domains: data.allowedDomains,
          agent_id: data.agentId,
          messages_per_minute: 10,
        },
      })
    },
    onSuccess: () => {
      toast.success('Widget updated successfully')
      refetchWidget()
    },
    onError: (error) => {
      console.error(error)
      toast.error('Error creating widget')
    },
  })

  const { mutateAsync: createAgent, isPending: isCreatingAgent } = useMutation({
    mutationFn: async () => $fetch('/v1/agents', {
      method: 'POST',
      body: {
        voice: config.voice,
        prompt: `You are a helpful assistant. Have a natural conversation with the user. Be friendly and engaging. Keep responses concise and conversational.`,
        pathway_id: config.pathwayId,
      },
    }),
    onError: (error) => {
      console.error(error)
      toast.error('Error creating agent')
    },
  })

  const { mutate: updateAgent, isPending: isUpdatingAgent } = useMutation({
    mutationFn: async data => $fetch(`/v1/agents/${data.agentId}`, {
      method: 'POST',
      body: {
        voice: data.voice,
        pathway_id: data.pathwayId,
      },
    }),
    onError: (error) => {
      console.error(error)
      toast.error('Error updating agent')
    },
  })

  const { mutate: createWidget, isPending: isCreating } = useMutation({
    mutationFn: async (data) => {
      return $fetch('/v1/widget', {
        method: 'POST',
        body: {
          config: stripDataForConfig(data),
          agent_id: data.agentId,
          pathway_id: data.pathwayId,
          allowed_domains: data.allowedDomains,
          messages_per_minute: 10,
        },
      })
    },
    onSuccess: () => {
      toast.success('Widget created successfully')
      refetchWidget()
    },
    onError: (error) => {
      console.error(error)
      toast.error('Error creating widget')
    },
  })

  const handleSave = async () => {
    let currentAgentId = widgetData?.agent_id
    if (currentAgentId) {
      updateAgent({ agentId: currentAgentId, ...config })
    }
    else {
      const data = await createAgent()
      currentAgentId = data.agent.agent_id
    }

    if (widgetExists) {
      updateWidget({ agentId: currentAgentId, ...config })
    }
    else {
      createWidget({ agentId: currentAgentId, ...config })
    }
  }

  const isLoading = isLoadingWidget || isLoadingPathways
  const isPending = isCreating || isUpdating || isCreatingAgent || isUpdatingAgent

  const dataForWebComponent = JSON.stringify(config)
  return (
    <div className="grid grid-cols-[4fr_3fr]">
      <div className="p-5">
        <div id="widget-config-web-component" />
        <link href={`${CDN_BASE}/web-component/widget-web-component.css`} rel="stylesheet" />

        <TopBarContent>
          <TopBar.Title>Web Widget</TopBar.Title>
          <TopBar.Tabs selectedKey={tab} onSelectionChange={handleTabChange}>
            <TopBar.Tabs.List>
              {tabs.map(tabItem => (
                <TopBar.Tabs.Tab key={tabItem.value} id={tabItem.value}>
                  {tabItem.label}
                </TopBar.Tabs.Tab>
              ))}
            </TopBar.Tabs.List>
          </TopBar.Tabs>
          <TopBar.Actions>
            <Button
              variant="primary"
              size="sm"
              onPress={() => navigate(`/dashboard/web-widget/${config.widgetId}/logs`)}
            >
              View Widget Logs
            </Button>
          </TopBar.Actions>
        </TopBarContent>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSave)}>
            <div className="mt-5 flex h-full">
              {isLoading
                ? (
                    <div className={`
                      mt-32 flex h-full w-full items-center justify-center
                    `}
                    >
                      <GradientLoadingAnimation message="Loading Widget" variant="roseGarden" rounded="full" />
                    </div>
                  )
                : (
                    <div className="flex h-full w-full flex-col gap-5">
                      {tab === 'settings' && (
                        <WidgetSettings
                          pathways={pathways}
                          isLoadingPathways={isLoadingPathways}
                          widgetId={config.widgetId}
                        />
                      )}
                      {tab === 'dimensions' && (
                        <WidgetPersonalization config={config} />
                      )}

                      <div className="pb-10">
                        <Button
                          type="submit"
                          isLoading={isPending}
                          className="w-fit shrink-0 bg-blue-500 text-white"
                          variant="solid"
                          radius="none"
                        >
                          {widgetExists ? 'Update widget' : 'Save widget'}
                        </Button>
                      </div>
                    </div>
                  )}
            </div>
          </form>
        </FormProvider>
      </div>
      <div className="relative">
        <DottedBackground />
        {!isLoading && <widget-component data={dataForWebComponent} />}
      </div>
    </div>
  )
}

function stripDataForConfig(data) {
  const fieldsToStrip = ['id', 'allowedDomains', 'threads', 'pathwayId', 'widgetId', 'agentId']
  return Object.fromEntries(Object.entries(data).filter(([key]) => !fieldsToStrip.includes(key)))
}
